import { ImageIcon, ThumbsUp } from 'lucide-react'
import { CSSProperties, useState, useEffect } from 'react'

import { Image } from '../ui'
import { Badge } from '../ui/badge'
import { cn } from '../ui/utils'
import { useLocalization } from '../../hooks/useLocalization'
import { IMAGE_URLS } from 'src/assets/urlLinks'
import { useLikesApi } from 'src/api/db/apiLikes'
import { DesignImage } from '../Design'
import { Design_ } from 'src/api/db/apiDesigns'
import { useGetOrgId } from 'src/api/helpers/getOrgId'
import { useOrgsApi } from 'src/api/db/apiOrgs'

export const ProductImage = ({
  imageClassName,
  containerClassName,
  onClick,
  image_url,
  style,
  onImageLoad,
  klayder_recommends,
  product_id,
  design,
}: {
  imageClassName?: CSSProperties | string
  containerClassName?: CSSProperties | string
  onClick?: () => void
  image_url: string | null | undefined
  style?: CSSProperties
  onImageLoad?: () => void
  klayder_recommends?: boolean
  product_id?: number
  design?: Design_
}) => {
  const { t } = useLocalization()
  const [imageError, setImageError] = useState(false)

  const org_id = useGetOrgId()
  const { organizationById } = useOrgsApi({
    org_id,
  })
  const organization = organizationById?.data
  const chosen_logo_url = organization?.chosen_logo_url

  const { likeCounts } = useLikesApi({
    productIds: product_id ? [product_id] : undefined,
  })
  const likeCount = product_id ? likeCounts.get(product_id) || 0 : 0

  // If there's no image URL, call onImageLoad immediately so UI doesn't get stuck
  useEffect(() => {
    if (!image_url && onImageLoad) {
      onImageLoad()
    }
  }, [image_url, onImageLoad])

  return (
    <>
      <div
        className={cn(
          'relative overflow-hidden',
          containerClassName,
          onClick && 'cursor-pointer '
        )}
        style={style}
        onClick={onClick}
      >
        {imageError || !image_url ? (
          <div className="w-full h-full flex items-center justify-center rounded-lg">
            <div
              className={cn(
                'flex flex-col items-center justify-center text-gray-400 h-full w-full',
                imageClassName
              )}
            >
              <ImageIcon className="w-1/3 h-1/3 max-w-16 max-h-16" />
            </div>
          </div>
        ) : (
          <DesignImage
            onImageError={() => {
              setImageError(true)
              onImageLoad?.() // Call even on error so UI doesn't get stuck
            }}
            onImageLoad={() => {
              onImageLoad?.()
            }}
            logo_url={chosen_logo_url || null}
            image_url={image_url || ''}
            design={design as Omit<Design_, 'design_url' | 'image_url'>}
            key={0}
            containerClassName={containerClassName ?? 'rounded-2xl'}
            imageClassName={imageClassName ?? 'object-contain max-h-[300px]'}
          />
        )}

        {klayder_recommends && (
          <Badge
            variant="secondary"
            className="absolute top-2.5 left-2.5 gap-1"
          >
            <Image
              src={IMAGE_URLS.logos.klayderSymbol}
              alt="Klayder Symbol"
              className="w-3.5 h-3.5 invert"
            />
            {t('We recommend', 'Vi rekommenderar')}
          </Badge>
        )}

        {product_id !== undefined && likeCount > 0 && (
          <Badge
            variant="secondary"
            className="absolute top-2.5 right-2.5 gap-1"
          >
            {likeCount}
            <ThumbsUp className="w-3 h-3" />
          </Badge>
        )}
      </div>
    </>
  )
}
